import { Box } from '@mantine/core'

import { isBudgetCategory } from '@/types/budgets.types'

import { BudgetDonutChart } from '@/components/BudgetDonutChart/BudgetDonutChart'
import type { BudgetsWidgetItemProps } from '@/components/BudgetsWidgetItem/BudgetsWidgetItem.types'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { BUDGET_CATEGORIES } from '@/constants/budgetCategories.constants'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { mapCategories } from '@/helpers/mapCategories.helper'
import { sanitizeEnum } from '@/helpers/sanitizeEnum.helper'

import classes from './BudgetsWidgetItem.module.css'

export function BudgetsWidgetItem({ budget }: BudgetsWidgetItemProps) {
  const currentCategory = BUDGET_CATEGORIES.find(
    category => category.name === budget.category,
  )

  if (!currentCategory) {
    return null
  }

  const percentageSpent = +(
    (budget.spent / budget.monthlyAmount) *
    100
  ).toFixed(2)

  const chartData = [
    {
      color: currentCategory.color,
      name: 'Spent',
      value: percentageSpent > 100 ? 100 : percentageSpent,
    },
    {
      color: 'var(--mantine-other-colors-light-grey)',
      name: 'Remaining',
      value: percentageSpent > 100 ? 0 : 100 - percentageSpent,
    },
  ]

  return (
    <Box className={classes.container}>
      <Box className={classes.chart}>
        <BudgetDonutChart data={chartData} />

        <currentCategory.icon className={classes.categoryIcon} />
      </Box>

      <Text
        className={classes.categoryName}
        lineClamp={1}
        variant="body_2_medium_black"
      >
        {isBudgetCategory(budget.category)
          ? mapCategories(budget.category)
          : sanitizeEnum(budget.category)}
      </Text>

      <Text lineClamp={1} variant="body_4_gray">
        {formatMoney(budget.spent, 'USD')} Spent
      </Text>

      <Text lineClamp={1} variant="body_4_gray">
        {formatMoney(budget.remaining, 'USD')} Remaining
      </Text>

      <Title lineClamp={1} mt={8} variant="h2">
        {formatMoney(+budget.monthlyAmount, 'USD')}
      </Title>
    </Box>
  )
}
